import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Filter, Plus, Trash2, Users, Send, Loader2, Eye, X, Phone, Footprints, FileText } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/app/PageHeader";
import { Loader, EmptyState } from "@/components/app/Loader";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

const FIELDS = [
  { v: "turnoutScore", t: "num" }, { v: "supportScore", t: "num" }, { v: "persuasionScore", t: "num" },
  { v: "age", t: "num" }, { v: "party", t: "str" }, { v: "ward", t: "str" }, { v: "city", t: "str" }, { v: "zip", t: "str" },
];
const OPS = ["=", "!=", ">=", "<=", "between", "contains"];

export default function Segments() {
  const { hasRole } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["segments"], queryFn: () => api.get("/audience/segments").then((r) => r.data) });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/audience/segments/${id}`),
    onSuccess: () => { toast.success("Segment deleted"); qc.invalidateQueries({ queryKey: ["segments"] }); },
  });

  const exportList = async (segId, type, name, fmt = "csv") => {
    try {
      const r = await api.get(`/voters/cut-list`, { params: { list_type: type, segment_id: segId, fmt }, responseType: "blob" });
      const href = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      const ext = fmt === "pdf" ? "pdf" : "csv";
      a.href = href;
      a.download = `${type}-${fmt === "pdf" ? "sheet" : "list"}_${(name || "segment").toLowerCase().replace(/\s+/g, "-")}.${ext}`;
      a.click();
      URL.revokeObjectURL(href);
      toast.success(`${type === "walk" ? "Walk" : "Call"} ${fmt === "pdf" ? "sheet (PDF)" : "list"} exported`);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader
        title="Segments"
        subtitle="Rule-based audiences over your voter universe. Where members are also opt-in contacts, segments double as email targeting."
        icon={Filter}
        actions={hasRole("admin", "manager") && <SegmentDialog />}
      />

      {isLoading ? (
        <Loader />
      ) : !data?.length ? (
        <EmptyState icon={Filter} title="No segments yet" description="Create a rule-based segment over your voters." />
      ) : (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {data.map((s) => (
            <div key={s.id} className="rounded-xl border border-border bg-white p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md" data-testid="segment-card">
              <div className="flex items-start justify-between">
                <h3 className="font-heading text-lg font-bold text-brand-ink">{s.name}</h3>
                {hasRole("admin", "manager") && (
                  <Button variant="ghost" size="icon" onClick={() => del.mutate(s.id)} data-testid={`delete-segment-${s.id}`}><Trash2 className="h-4 w-4 text-brand-red" /></Button>
                )}
              </div>
              <p className="mt-1 text-sm text-brand-mute">{s.description}</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {s.rules?.map((r, i) => (
                  <Badge key={i} variant="outline" className="font-mono text-[0.65rem] text-brand-navy">
                    {r.field} {r.op} {Array.isArray(r.value) ? r.value.join(" to ") : r.value}
                  </Badge>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-4 border-t border-border pt-3">
                <span className="flex items-center gap-1.5 text-sm font-semibold text-brand-ink"><Users className="h-4 w-4 text-brand-navy" /> {s.voter_count?.toLocaleString()} voters</span>
                {s.persona_label && <Badge className="bg-brand-red/10 text-brand-red">{s.persona_label}</Badge>}
              </div>
              <div className="mt-3">
                <div className="mb-1 flex items-center justify-between text-xs">
                  <span className="font-medium text-brand-mute">Canvass coverage</span>
                  <span className="font-semibold text-brand-ink">{s.coverage_pct ?? 0}% · {s.canvassed_count ?? 0}/{s.voter_count ?? 0}</span>
                </div>
                <div className="h-2 w-full rounded-full bg-slate-100">
                  <div className="h-2 rounded-full transition-all" style={{ width: `${Math.min(100, s.coverage_pct ?? 0)}%`, background: (s.coverage_pct ?? 0) >= 60 ? "#1E7E34" : (s.coverage_pct ?? 0) >= 25 ? "#B8860B" : "#E63946" }} />
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => exportList(s.id, "walk", s.name)} data-testid={`walk-list-${s.id}`} className="hover:border-brand-navy hover:text-brand-navy">
                  <Footprints className="mr-1.5 h-3.5 w-3.5" /> Walk CSV
                </Button>
                <Button variant="outline" size="sm" onClick={() => exportList(s.id, "walk", s.name, "pdf")} data-testid={`walk-pdf-${s.id}`} className="hover:border-brand-red hover:text-brand-red">
                  <FileText className="mr-1.5 h-3.5 w-3.5" /> Walk PDF
                </Button>
                <Button variant="outline" size="sm" onClick={() => exportList(s.id, "call", s.name)} data-testid={`call-list-${s.id}`} className="hover:border-brand-blue hover:text-brand-blue">
                  <Phone className="mr-1.5 h-3.5 w-3.5" /> Call CSV
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SegmentDialog() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [rules, setRules] = useState([{ field: "turnoutScore", op: ">=", value: "55" }]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const buildRules = () =>
    rules.map((r) => {
      const meta = FIELDS.find((f) => f.v === r.field);
      let value = r.value;
      if (r.op === "between") value = String(r.value).split(",").map((x) => Number(x.trim()));
      else if (meta?.t === "num") value = Number(r.value);
      return { field: r.field, op: r.op, value };
    });

  const doPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/audience/segments/evaluate", { rules: buildRules() });
      setPreview(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Name your segment");
    setBusy(true);
    try {
      await api.post("/audience/segments", { name, description: desc, rules: buildRules() });
      toast.success("Segment saved");
      qc.invalidateQueries({ queryKey: ["segments"] });
      setOpen(false); setName(""); setDesc(""); setPreview(null);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-brand-red text-white hover:bg-brand-redhover" data-testid="new-segment-btn"><Plus className="mr-2 h-4 w-4" /> New segment</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">Build a segment</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="High-turnout persuadables in Ward 2" className="mt-1.5" data-testid="segment-name-input" /></div>
          <div><Label>Description</Label><Input value={desc} onChange={(e) => setDesc(e.target.value)} className="mt-1.5" /></div>
          <div>
            <Label>Rules</Label>
            <div className="mt-1.5 space-y-2">
              {rules.map((r, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <Select value={r.field} onValueChange={(v) => setRules((rs) => rs.map((x, j) => j === i ? { ...x, field: v } : x))}>
                    <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
                    <SelectContent>{FIELDS.map((f) => <SelectItem key={f.v} value={f.v}>{f.v}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={r.op} onValueChange={(v) => setRules((rs) => rs.map((x, j) => j === i ? { ...x, op: v } : x))}>
                    <SelectTrigger className="w-[90px]"><SelectValue /></SelectTrigger>
                    <SelectContent>{OPS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input value={r.value} onChange={(e) => setRules((rs) => rs.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} placeholder={r.op === "between" ? "40,68" : "value"} className="flex-1" />
                  {rules.length > 1 && <Button variant="ghost" size="icon" onClick={() => setRules((rs) => rs.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>}
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setRules((rs) => [...rs, { field: "ward", op: "=", value: "" }])}><Plus className="mr-1 h-3.5 w-3.5" /> Add rule</Button>
            </div>
          </div>
          {preview && (
            <div className="rounded-lg border border-border bg-slate-50 p-3 text-sm">
              <p className="font-semibold text-brand-ink">{preview.voter_count.toLocaleString()} voters match</p>
              <p className="text-brand-mute">{preview.sendable_contacts} are sendable opt-in contacts</p>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={doPreview} disabled={busy} data-testid="preview-segment-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="mr-1.5 h-4 w-4" />} Preview</Button>
          <Button onClick={save} disabled={busy} className="bg-brand-red text-white hover:bg-brand-redhover" data-testid="save-segment-btn">Save segment</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
